import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { SweepNotification } from '../../reducers/notificationsReducer';

interface PersistentNotificationsContextValue {
  notifications: SweepNotification[];
  setNotifications: (sourceKey: string, notifications: SweepNotification[]) => void;
  removeSource: (sourceKey: string) => void;
}

const PersistentNotificationsContext = createContext<PersistentNotificationsContextValue>({
  notifications: [],
  setNotifications: () => {},
  removeSource: () => {},
});

export const PersistentNotificationsProvider = ({ children }: { children: React.ReactNode }) => {
  const [map, setMap] = useState<Map<string, SweepNotification[]>>(new Map());

  const setNotifications = useCallback((sourceKey: string, notifications: SweepNotification[]) => {
    setMap((prev) => new Map(prev).set(sourceKey, notifications));
  }, []);

  const removeSource = useCallback((sourceKey: string) => {
    setMap((prev) => {
      if (!prev.has(sourceKey)) return prev;
      const next = new Map(prev);
      next.delete(sourceKey);
      return next;
    });
  }, []);

  const notifications = useMemo(() => Array.from(map.values()).flat(), [map]);

  const value = useMemo(
    () => ({ notifications, setNotifications, removeSource }),
    [notifications, setNotifications, removeSource],
  );

  return (
    <PersistentNotificationsContext.Provider value={value}>
      {children}
    </PersistentNotificationsContext.Provider>
  );
};

export const usePersistentNotificationsContext = () => useContext(PersistentNotificationsContext);

export const useRegisterPersistentNotifications = (
  sourceKey: string,
  notifications: SweepNotification[],
) => {
  const { setNotifications, removeSource } = usePersistentNotificationsContext();

  useEffect(() => {
    setNotifications(sourceKey, notifications);
  }, [sourceKey, notifications, setNotifications]);

  useEffect(() => {
    return () => {
      removeSource(sourceKey);
    };
    // Only run cleanup on unmount — sourceKey must be stable (constant string at call site)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
